import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import ConfirmModal from '../../../components/ConfirmModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Компонент для сортируемого дела
const SortableTodoItem = ({ todo, onStatusChange, onEdit, onDelete, editingId, setEditingId, editingText, setEditingText, saveEditing, cancelEditing, startEditing, showDeleteConfirm, updatingStatus, currentLang, formatDate, formatCompletionDate, moveTodoUp, moveTodoDown, isFirstTodo, isLastTodo }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: todo.id,
    data: {
      type: 'todo',
      todo: todo,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 ${
        todo.status === 1 ? 'opacity-75' : ''
      } ${isDragging ? 'shadow-xl scale-105 rotate-2 z-10' : ''}`}
    >
      <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4">

        {/* Кнопки сортировки для мобильных */}
        <div className="flex flex-col gap-0.5 sm:hidden">
          <button
            onClick={() => moveTodoUp(todo.id)}
            disabled={isFirstTodo(todo.id)}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вверх' : 'Move up'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={() => moveTodoDown(todo.id)}
            disabled={isLastTodo(todo.id)}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вниз' : 'Move down'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {/* Иконка для перетаскивания */}
        <div
          {...listeners}
          className="hidden sm:flex flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-full items-center justify-center cursor-move transition-all duration-200 hover:scale-110 active:scale-95 touch-manipulation"
          title={currentLang === 'ru' ? 'Перетащить для сортировки' : 'Drag to sort'}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Чекбокс */}
        <button
          onClick={() => onStatusChange(todo.id, todo.status === 1 ? 0 : 1)}
          disabled={updatingStatus.has(todo.id)}
          className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 transition-all duration-200 ${
            todo.status === 1
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-gray-300 hover:border-green-400 dark:border-gray-600'
          } ${updatingStatus.has(todo.id) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {updatingStatus.has(todo.id) ? (
            <div className="animate-spin rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 border-b-2 border-current mx-auto mt-0.5"></div>
          ) : todo.status === 1 ? (
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          ) : null}
        </button>

        {/* Текст дела */}
        <div className="flex-1 min-w-0">
          {editingId === todo.id ? (
            <div className="flex flex-col items-start sm:flex-row gap-2">
              <div>
                <textarea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) saveEditing(todo.id);
                    if (e.key === 'Escape') cancelEditing();
                  }}
                  className="edit flex-1 px-2 sm:px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm w-full"
                  autoFocus
                  rows={5}
                />
                <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                  {[
                    '👍', '👎', '👌', '✅', '❌', '⭐', 
                    '❗', '❓', '⚠️', '💡', '📌', '🎯',
                    '✨', '🔥', '⚡', '💪', '🎉', '✍️',
                    '➡️', '⬅️', '⬆️', '⬇️', '↩️', '↪️',
                    '😀', '😉', '🥰','😎', '🤩', '🥳',
                  ].map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        const textareaEdit = document.querySelector('textarea.edit');
                        const cursorPosEdit  = textareaEdit.selectionStart;
                        const textBeforeEdit = editingText.substring(0, cursorPosEdit);
                        const textAfterEdit = editingText.substring(cursorPosEdit);
                        setEditingText(textBeforeEdit + emoji + textAfterEdit);
                         // Устанавливаем фокус обратно на textarea и восстанавливаем позицию курсора
                        textareaEdit.focus();
                        const newCursorPosEdit = cursorPosEdit + emoji.length;
                        textareaEdit.setSelectionRange(newCursorPosEdit, newCursorPosEdit);
                      }}
                      className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      title={currentLang === 'ru' ? 'Вставить символ' : 'Insert symbol'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1 sm:gap-2">
                <button
                  onClick={() => saveEditing(todo.id)}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs sm:text-sm"
                >
                  {currentLang === 'ru' ? 'Сохранить' : 'Save'}
                </button>
                <button
                  onClick={cancelEditing}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-xs sm:text-sm"
                >
                  {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <span
                className={`flex-1 text-sm sm:text-base text-gray-900 dark:text-white ${
                  todo.status === 1 ? 'line-through text-gray-500' : ''
                }`}
              >
                {todo.text.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < todo.text.split('\n').length - 1 && <br/>}
                  </React.Fragment>
                ))}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatCompletionDate(todo.created_at)}
              </span>
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        {editingId !== todo.id && (
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => startEditing(todo)}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-colors dark:hover:bg-blue-900/20"
              title={currentLang === 'ru' ? 'Редактировать' : 'Edit'}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => showDeleteConfirm(todo)}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors dark:hover:bg-red-900/20"
              title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Todo = () => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  // Состояние для данных
  const [todos, setTodos] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для переключения между активными и завершенными делами
  const [viewMode, setViewMode] = useState('active'); // 'active' или 'completed'
  
  // Состояние для фильтров завершенных дел
  const [completedFilters, setCompletedFilters] = useState({
    date_from: '',
    date_to: ''
  });
  
  // Состояние для сортировки завершенных дел
  const [completedSort, setCompletedSort] = useState('newest'); // 'newest' или 'oldest'
  
  // Состояние для загрузки завершенных дел
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  
  // Состояние для нового дела
  const [newTodoText, setNewTodoText] = useState('');
  const [addingTodo, setAddingTodo] = useState(false);
  
  // Состояние для редактирования
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  
  // Состояние для обновления статуса
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  
  // Состояние для модалки удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState(null);

  // Состояние для обновления сортировки
  const [updatingSort, setUpdatingSort] = useState(false);

  // Настройка сенсоров для Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Загрузка данных
  const fetchTodos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos`);
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTodos(data.data || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки списка дел');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Загружаем данные при монтировании
  useEffect(() => {
    fetchTodos();
  }, []);
  
  // Загружаем завершенные дела при изменении фильтров
  useEffect(() => {
    if (viewMode === 'completed') {
      // Устанавливаем фильтр по умолчанию (последние 7 дней) при первом переключении
      if (!completedFilters.date_from && !completedFilters.date_to) {
        const range = getLast7DaysRange();
        setCompletedFilters({ date_from: range.from, date_to: range.to });
      } else {
        fetchCompletedTodos();
      }
    }
  }, [viewMode, completedFilters]);

  // Загрузка завершенных дел
  const fetchCompletedTodos = async () => {
    try {
      setLoadingCompleted(true);
      setError(null);
      
      // Формируем параметры запроса
      const params = new URLSearchParams();
      
      // Добавляем фильтры дат, если они заполнены
      if (completedFilters.date_from) {
        params.append('date_from', completedFilters.date_from);
      }
      if (completedFilters.date_to) {
        params.append('date_to', completedFilters.date_to);
      }
      
      const url = `${API_URL}${currentLang}/todos/completed${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await fetchWithAuth(url);
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setCompletedTodos(data.data || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки завершенных дел');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoadingCompleted(false);
    }
  };

  // Создание нового дела
  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (!newTodoText.trim()) return;

    try {
      setAddingTodo(true);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify({ text: newTodoText.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Добавляем новое дело в начало списка
        const newTodo = {
          id: data.data.id,
          user_id: user.id,
          text: newTodoText.trim(),
          status: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        setTodos(prev => [newTodo, ...prev]);
        setNewTodoText('');
        
        showNotification(
          currentLang === 'ru' ? 'Дело добавлено!' : 'Todo added!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка создания дела');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setAddingTodo(false);
    }
  };

  // Обновление статуса дела
  const handleStatusChange = async (todoId, newStatus) => {
    try {
      setUpdatingStatus(prev => new Set(prev).add(todoId));
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos/${todoId}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем дело в списке
        setTodos(prev => prev.map(todo => 
          todo.id === todoId 
            ? { ...todo, status: newStatus, updated_at: new Date().toISOString() }
            : todo
        ));
        
        showNotification(
          newStatus === 1 
            ? (currentLang === 'ru' ? 'Дело выполнено!' : 'Todo completed!')
            : (currentLang === 'ru' ? 'Дело отмечено как невыполненное' : 'Todo marked as incomplete'),
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка обновления статуса');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(todoId);
        return newSet;
      });
    }
  };

  // Начало редактирования
  const startEditing = (todo) => {
    setEditingId(todo.id);
    setEditingText(todo.text);
  };

  // Сохранение редактирования
  const saveEditing = async (todoId) => {
    if (!editingText.trim() || editingText === todos.find(t => t.id === todoId)?.text) {
      setEditingId(null);
      setEditingText('');
      return;
    }

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos/${todoId}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify({ text: editingText.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем дело в списке
        setTodos(prev => prev.map(todo => 
          todo.id === todoId 
            ? { ...todo, text: editingText.trim(), updated_at: new Date().toISOString() }
            : todo
        ));
        
        setEditingId(null);
        setEditingText('');
        
        showNotification(
          currentLang === 'ru' ? 'Дело обновлено!' : 'Todo updated!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка обновления дела');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  // Отмена редактирования
  const cancelEditing = () => {
    setEditingId(null);
    setEditingText('');
  };

  // Показать модалку удаления
  const showDeleteConfirm = (todo) => {
    setTodoToDelete(todo);
    setShowDeleteModal(true);
  };

  // Подтверждение удаления
  const handleDeleteConfirm = async () => {
    if (!todoToDelete) return;

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos/${todoToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Удаляем дело из соответствующего списка в зависимости от режима просмотра
        if (viewMode === 'active') {
          setTodos(prev => prev.filter(todo => todo.id !== todoToDelete.id));
        } else if (viewMode === 'completed') {
          setCompletedTodos(prev => prev.filter(todo => todo.id !== todoToDelete.id));
        }
        
        showNotification(
          currentLang === 'ru' ? 'Дело удалено!' : 'Todo deleted!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка удаления дела');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setShowDeleteModal(false);
      setTodoToDelete(null);
    }
  };

  // Закрытие модалки удаления
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTodoToDelete(null);
  };

  // Обновление сортировки дел
  const updateTodoSort = async (newTodos) => {
    try {
      setUpdatingSort(true);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Формируем объект с порядком дел
      const todoOrders = {};
      newTodos.forEach((todo, index) => {
        todoOrders[index + 1] = todo.id;
      });

      const response = await fetchWithAuth(`${API_URL}${currentLang}/todos/sort`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify({ todo_orders: todoOrders }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTodos(newTodos);
        showNotification(
          currentLang === 'ru' ? 'Порядок дел обновлен!' : 'Todo order updated!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка обновления сортировки');
      }
    } catch (error) {
      showNotification(error.message, 'error');
      // Возвращаем исходный порядок при ошибке
      fetchTodos();
    } finally {
      setUpdatingSort(false);
    }
  };

  // Функции для перемещения дел (для мобильных устройств)
  const moveTodoUp = async (todoId) => {
    const currentIndex = todos.findIndex(todo => todo.id === todoId);
    if (currentIndex <= 0) return;

    const newTodos = [...todos];
    const temp = newTodos[currentIndex];
    newTodos[currentIndex] = newTodos[currentIndex - 1];
    newTodos[currentIndex - 1] = temp;

    await updateTodoSort(newTodos);
  };

  const moveTodoDown = async (todoId) => {
    const currentIndex = todos.findIndex(todo => todo.id === todoId);
    if (currentIndex >= todos.length - 1) return;

    const newTodos = [...todos];
    const temp = newTodos[currentIndex];
    newTodos[currentIndex] = newTodos[currentIndex + 1];
    newTodos[currentIndex + 1] = temp;

    await updateTodoSort(newTodos);
  };

  const isFirstTodo = (todoId) => {
    return todos.findIndex(todo => todo.id === todoId) === 0;
  };

  const isLastTodo = (todoId) => {
    return todos.findIndex(todo => todo.id === todoId) === todos.length - 1;
  };

  // Функция показа уведомлений
  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Функции для работы с датами
  const getLast7DaysRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6); // Изменено с -7 на -6, чтобы включить сегодняшний день
    
    // Используем локальные даты
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(start),
      to: formatDate(end)
    };
  };

  const getLast30DaysRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    // Используем локальные даты
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(start),
      to: formatDate(end)
    };
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Используем локальные даты
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(start),
      to: formatDate(end)
    };
  };

  const getTodayRange = () => {
    const today = new Date();
    // Используем локальную дату, а не UTC
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    
    return {
      from: todayStr,
      to: todayStr
    };
  };

  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    setCompletedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setCompletedFilters({
      date_from: '',
      date_to: ''
    });
  };

  // Функция сортировки завершенных дел
  const getSortedCompletedTodos = () => {
    return [...completedTodos].sort((a, b) => {
      const dateA = new Date(a.updated_at);
      const dateB = new Date(b.updated_at);
      
      if (completedSort === 'newest') {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      
      // Сравниваем только даты, игнорируя время
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffTime = nowOnly - dateOnly;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return currentLang === 'ru' ? 'Вчера' : 'Yesterday';
      } else if (diffDays < 7) {
        return date.toLocaleDateString('ru-RU', { weekday: 'short' });
      } else {
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
      }
    } catch (error) {
      return dateString;
    }
  };

  // Форматирование даты завершения (более точное)
  const formatCompletionDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="p-2 sm:p-4 max-w-4xl mx-auto">
      {/* Заголовок */}
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {currentLang === 'ru' ? 'Список дел' : 'Todo List'}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {currentLang === 'ru' ? 'Организуйте свои задачи быстро и эффективно' : 'Organize your tasks quickly and efficiently'}
        </p>
      </div>

      {/* Переключатель режимов */}
      <div className="mb-4 sm:mb-6">
        <div className="flex bg-gray-200 rounded-lg p-1 w-full sm:w-fit dark:bg-gray-700">
          <button
            onClick={() => setViewMode('active')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              viewMode === 'active' 
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' 
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            {currentLang === 'ru' ? 'Активные' : 'Active'}
          </button>
          <button
            onClick={() => setViewMode('completed')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              viewMode === 'completed' 
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' 
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            {currentLang === 'ru' ? 'Завершенные' : 'Completed'}
          </button>
        </div>
      </div>

      {/* Фильтры для завершенных дел */}
      {viewMode === 'completed' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {currentLang === 'ru' ? 'Фильтры по дате' : 'Date Filters'}
            </h3>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Селектор сортировки */}
              <select
                value={completedSort}
                onChange={(e) => setCompletedSort(e.target.value)}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="newest">
                  {currentLang === 'ru' ? 'Сначала новые' : 'Newest first'}
                </option>
                <option value="oldest">
                  {currentLang === 'ru' ? 'Сначала старые' : 'Oldest first'}
                </option>
              </select>
              <button
                onClick={clearFilters}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md dark:hover:bg-red-900/20"
              >
                {currentLang === 'ru' ? 'Очистить' : 'Clear'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                {currentLang === 'ru' ? 'Дата начала' : 'Start Date'}
              </label>
              <input
                type="date"
                value={completedFilters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                {currentLang === 'ru' ? 'Дата окончания' : 'End Date'}
              </label>
              <input
                type="date"
                value={completedFilters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              onClick={() => {
                const range = getTodayRange();
                setCompletedFilters({ date_from: range.from, date_to: range.to });
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                completedFilters.date_from === getTodayRange().from && completedFilters.date_to === getTodayRange().to
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
              }`}
            >
              {currentLang === 'ru' ? 'Сегодня' : 'Today'}
            </button>
            <button
              onClick={() => {
                const range = getLast7DaysRange();
                setCompletedFilters({ date_from: range.from, date_to: range.to });
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                completedFilters.date_from === getLast7DaysRange().from && completedFilters.date_to === getLast7DaysRange().to
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Последние 7 дней' : 'Last 7 days'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? '7 дней' : '7 days'}</span>
            </button>
            <button
              onClick={() => {
                const range = getLast30DaysRange();
                setCompletedFilters({ date_from: range.from, date_to: range.to });
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                completedFilters.date_from === getLast30DaysRange().from && completedFilters.date_to === getLast30DaysRange().to
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Последние 30 дней' : 'Last 30 days'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? '30 дней' : '30 days'}</span>
            </button>
            <button
              onClick={() => {
                const range = getCurrentMonthRange();
                setCompletedFilters({ date_from: range.from, date_to: range.to });
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                completedFilters.date_from === getCurrentMonthRange().from && completedFilters.date_to === getCurrentMonthRange().to
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Этот месяц' : 'This month'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Месяц' : 'Month'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Форма добавления */}
      {viewMode === 'active' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6 dark:bg-gray-800 dark:border-gray-700">
        <form onSubmit={handleAddTodo} className="flex flex-col items-start sm:flex-row gap-3">
          <div>
            <textarea
              value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
              placeholder={currentLang === 'ru' ? 'Что нужно сделать?' : 'What needs to be done?'}
              className="add w-full flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 text-sm sm:text-base"
              disabled={addingTodo}
              rows={5}
            />
            <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
              {[
                '👍', '👎', '👌', '✅', '❌', '⭐', 
                '❗', '❓', '⚠️', '💡', '📌', '🎯',
                '✨', '🔥', '⚡', '💪', '🎉', '✍️',
                '➡️', '⬅️', '⬆️', '⬇️', '↩️', '↪️',
                '😀', '😉', '🥰','😎', '🤩', '🥳',
              ].map((emoji, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => {
                    const textarea = document.querySelector('textarea.add');
                    const cursorPos = textarea.selectionStart;
                    const textBefore = newTodoText.substring(0, cursorPos);
                    const textAfter = newTodoText.substring(cursorPos);
                    setNewTodoText(textBefore + emoji + textAfter);
                    // Устанавливаем фокус обратно на textarea и восстанавливаем позицию курсора
                    textarea.focus();
                    const newCursorPos = cursorPos + emoji.length;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                  }}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                  title={currentLang === 'ru' ? 'Вставить символ' : 'Insert symbol'}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={addingTodo || !newTodoText.trim()}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {addingTodo ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{currentLang === 'ru' ? 'Добавление...' : 'Adding...'}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{currentLang === 'ru' ? 'Добавить' : 'Add'}</span>
              </>
            )}
          </button>
        </form>
        </div>
      )}

      {/* Список дел */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
        {viewMode === 'active' ? (
          // Активные дела
          loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">
              {currentLang === 'ru' ? 'Загрузка списка дел...' : 'Loading todos...'}
            </p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchTodos}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {currentLang === 'ru' ? 'Попробовать снова' : 'Try again'}
            </button>
          </div>
        ) : todos.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {currentLang === 'ru' ? 'Список дел пуст' : 'Todo list is empty'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {currentLang === 'ru' ? 'Добавьте первое дело выше' : 'Add your first todo above'}
            </p>
          </div>
        ) : (
          <>
            {/* Подсказка для мобильных устройств */}
            <div className="p-3 bg-blue-50 border-b border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 sm:hidden">
              <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{currentLang === 'ru' ? 'Используйте стрелки для перемещения дел' : 'Use arrows to move items'}</span>
              </div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={async (event) => {
                const { active, over } = event;
                
                if (active && over && active.id !== over.id) {
                  const oldIndex = todos.findIndex(todo => todo.id === active.id);
                  const newIndex = todos.findIndex(todo => todo.id === over.id);
                  
                  if (oldIndex !== -1 && newIndex !== -1) {
                    const newTodos = arrayMove(todos, oldIndex, newIndex);
                    await updateTodoSort(newTodos);
                  }
                }
              }}
            >
            <SortableContext
              items={todos.map(todo => todo.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {updatingSort && (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {currentLang === 'ru' ? 'Обновление порядка...' : 'Updating order...'}
                    </p>
                  </div>
                )}
                {todos.map((todo) => (
                  <SortableTodoItem
                    key={todo.id}
                    todo={todo}
                    onStatusChange={handleStatusChange}
                    onEdit={startEditing}
                    onDelete={showDeleteConfirm}
                    editingId={editingId}
                    setEditingId={setEditingId}
                    editingText={editingText}
                    setEditingText={setEditingText}
                    saveEditing={saveEditing}
                    cancelEditing={cancelEditing}
                    startEditing={startEditing}
                    showDeleteConfirm={showDeleteConfirm}
                    updatingStatus={updatingStatus}
                    currentLang={currentLang}
                    formatDate={formatDate}
                    formatCompletionDate={formatCompletionDate}
                    moveTodoUp={moveTodoUp}
                    moveTodoDown={moveTodoDown}
                    isFirstTodo={isFirstTodo}
                    isLastTodo={isLastTodo}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          </>
        )
        ) : (
          // Завершенные дела
          loadingCompleted ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">
                {currentLang === 'ru' ? 'Загрузка завершенных дел...' : 'Loading completed todos...'}
              </p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchCompletedTodos}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {currentLang === 'ru' ? 'Попробовать снова' : 'Try again'}
              </button>
            </div>
          ) : completedTodos.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {currentLang === 'ru' ? 'Нет завершенных дел' : 'No completed todos'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {currentLang === 'ru' ? 'Завершенные дела появятся здесь' : 'Completed todos will appear here'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {getSortedCompletedTodos().map((todo) => (
                <div
                  key={todo.id}
                  className="p-3 sm:p-4 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 opacity-75"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Чекбокс (завершенный) */}
                    <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 bg-green-500 border-green-500 text-white flex items-center justify-center">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>

                    {/* Текст дела */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <span className="flex-1 text-sm sm:text-base text-gray-900 dark:text-white line-through text-gray-500">
                          {todo.text.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < todo.text.split('\n').length - 1 && <br/>}
                            </React.Fragment>
                          ))}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatCompletionDate(todo.updated_at)}
                        </span>
                      </div>
                    </div>

                    {/* Кнопка удаления */}
                    <button
                      onClick={() => showDeleteConfirm(todo)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors dark:hover:bg-red-900/20"
                      title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                    >
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={
          todoToDelete
            ? (currentLang === 'ru' 
                ? `Вы уверены, что хотите удалить дело "${todoToDelete.text}"? Это действие нельзя отменить.`
                : `Are you sure you want to delete the todo "${todoToDelete.text}"? This action cannot be undone.`
              )
            : ''
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default Todo; 